import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Nav from "@/components/Nav";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";

export default function Auth() {
  const [mode, setMode] = useState("register");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [refCode, setRefCode] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "register") {
        const u = await register(email, password, name, refCode || params.get("ref"));
        if (u?.free_case_available) {
          toast.success(`Welcome! You're founder #${u.founding_number || "in"} — your first case is on us.`);
        } else {
          toast.success("Account created. Let's start your case.");
        }
      } else {
        await login(email, password);
        toast.success("Welcome back");
      }
      const next = (() => { try { return sessionStorage.getItem("phr_post_auth_redirect"); } catch { return null; } })();
      if (next) { try { sessionStorage.removeItem("phr_post_auth_redirect"); } catch {} nav(next); }
      else nav("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFBFD]">
      <Nav />
      <div className="max-w-md mx-auto px-4 py-12">
        <Card className="trust-card">
          <CardContent className="p-8">
            <h1 className="text-2xl font-bold text-slate-900">{mode === "register" ? "Create your account" : "Welcome back"}</h1>
            <p className="text-sm text-slate-500 mt-1">{mode === "register" ? "Takes 20 seconds. No credit card yet." : "Sign in to continue your case."}</p>

            <form onSubmit={submit} className="mt-6 space-y-4">
              {mode === "register" && (
                <div>
                  <Label htmlFor="name">Your name</Label>
                  <Input id="name" data-testid="auth-name-input" value={name} onChange={(e)=>setName(e.target.value)} placeholder="Alex Rivera" required />
                </div>
              )}
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" data-testid="auth-email-input" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} placeholder="you@example.com" required />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" data-testid="auth-password-input" type="password" value={password} onChange={(e)=>setPassword(e.target.value)} placeholder="At least 6 characters" required minLength={6} />
              </div>
              {mode === "register" && (
                <div>
                  <Label htmlFor="ref">Referral code (optional)</Label>
                  <Input id="ref" data-testid="auth-referral-input" value={refCode} onChange={(e)=>setRefCode(e.target.value)} placeholder="friend's code" />
                </div>
              )}
              <Button data-testid="auth-submit-button" type="submit" className="w-full h-12 rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF]" disabled={busy}>
                {busy ? "Please wait…" : (mode === "register" ? "Create account" : "Sign in")}
              </Button>
            </form>

            <button data-testid="auth-toggle-mode-button" onClick={()=>setMode(mode === "register" ? "login" : "register")} className="mt-6 text-sm text-slate-600 hover:text-slate-900 w-full text-center">
              {mode === "register" ? "Already have an account? Sign in" : "New here? Create an account"}
            </button>
          </CardContent>
        </Card>
        <p className="text-xs text-slate-400 text-center mt-4"><Link to="/">← Back to home</Link></p>
      </div>
    </div>
  );
}
